import { useEffect, useState } from "react";
import { Alert, Box, Button, Skeleton, Stack, Typography } from "@mui/material";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import PurchaseOrderForm from "../CreatePurchaseOrder/PurchaseOrderForm/PurchaseOrderForm";
import PoConfirmDialog from "../CreatePurchaseOrder/PoConfirmDialog";
import {
	assertRequiredPoHeader,
	currentFinancialYearEnd,
	emptyPoForm,
	PO_API,
	poUrlKey,
	resolvePoHeaderLabels,
	todayPoDate,
} from "../CreatePurchaseOrder/data";
import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";
import {
	PoGinesysSyncBadge,
	PoPage,
	PoPageHeader,
	PoPanel,
	PoStatusBadge,
	poColors,
	poGhostBtnSx,
	poPrimaryBtnSx,
	poSecondaryBtnSx,
} from "../CreatePurchaseOrder/poUi";
import { reopenPurchaseOrderForEditApi, retryPurchaseOrderSyncApi, syncPurchaseOrderCostApi } from "../PoSyncLogs/data";
import PoSyncDetailsModal from "./PoSyncDetailsModal";
import { GlobalState } from "context/GlobalContext";

const toDateInput = (v) => (v ? String(v).slice(0, 10) : null);

/** Prefer a short human message when ginesys_last_error is a JSON blob. */
const syncErrorMessage = (raw) => {
	if (raw == null || raw === "") return "";
	const text = String(raw).trim();
	try {
		const parsed = JSON.parse(text);
		if (typeof parsed === "string") return parsed;
		if (parsed?.message) return String(parsed.message);
		if (typeof parsed?.error === "string") return parsed.error;
		if (parsed?.error?.message) return String(parsed.error.message);
		if (parsed?.result?.message) return String(parsed.result.message);
		if (parsed?.ErrorMessage) return String(parsed.ErrorMessage);
		return "See sync details for full error.";
	} catch {
		if ((text.startsWith("{") || text.startsWith("[")) && text.length > 180) {
			return "See sync details for full error.";
		}
		return text;
	}
};

const mapPoToForm = (po) => ({
	...emptyPoForm(),
	...po,
	order_date: po.status === "DRAFT" ? todayPoDate() : toDateInput(po.order_date),
	valid_from_date: toDateInput(po.valid_from_date),
	valid_to_date: toDateInput(po.valid_to_date) || currentFinancialYearEnd(),
	delivery_date: toDateInput(po.delivery_date || po.udf_date_01),
	channel: po.channel || po.udf_string_01 || null,
	trade_group_name: po.trade_group_name || (po.trade_group_code != null ? String(po.trade_group_code) : ""),
	items: (po.items || []).map((row) => ({
		...row,
		due_date: toDateInput(row.due_date),
	})),
	set_items: po.setItems || [],
	document_charges: po.documentCharges || [],
	line_charges: po.lineCharges || [],
	receive_schedules: po.receiveSchedules || [],
});

const SelectedPoLoading = () => (
	<PoPage sx={{ py: 1.25 }}>
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				justifyContent: "space-between",
				gap: 1.5,
				mb: 1.25,
				px: 1.75,
				py: 1.25,
				bgcolor: poColors.white,
				border: `1px solid ${poColors.line}`,
				borderRadius: "10px",
			}}>
			<Stack direction="row" spacing={1.25} alignItems="center">
				<Skeleton variant="rounded" width={180} height={22} />
				<Skeleton variant="rounded" width={72} height={22} />
				<Skeleton variant="rounded" width={96} height={18} />
			</Stack>
			<Stack direction="row" spacing={1} alignItems="center">
				<Skeleton variant="rounded" width={88} height={30} />
				<Skeleton variant="rounded" width={110} height={22} />
			</Stack>
		</Box>
		<PoPanel contentSx={{ pt: 1.75, pb: 1.75, px: 1.75 }}>
			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" },
					columnGap: 3,
					rowGap: 2,
				}}>
				{Array.from({ length: 12 }, (_, index) => (
					<Stack key={index} spacing={0.6}>
						<Skeleton variant="text" width="42%" height={16} />
						<Skeleton variant="rounded" width="78%" height={18} />
					</Stack>
				))}
			</Box>
		</PoPanel>
		<PoPanel sx={{ mt: 2 }} contentSx={{ px: 1.75, py: 1.5 }}>
			<Skeleton variant="text" width={140} height={22} sx={{ mb: 1 }} />
			{Array.from({ length: 4 }, (_, index) => (
				<Skeleton key={index} variant="rounded" height={36} sx={{ mb: 1 }} />
			))}
		</PoPanel>
	</PoPage>
);

const SelectedPurchaseOrder = () => {
	const [searchParams] = useSearchParams();
	const id = searchParams.get(poUrlKey);
	const { setLoadingOpen } = GlobalState();
	const [form, setForm] = useState(emptyPoForm());
	const [pageLoading, setPageLoading] = useState(true);
	const [masterLabels, setMasterLabels] = useState({});
	const [meta, setMeta] = useState({ status: "DRAFT", po_no: "", ginesys_sync: null, ginesys_po_id: null });
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState("");
	const [message, setMessage] = useState("");
	const [messageTone, setMessageTone] = useState("success");
	const [confirmAction, setConfirmAction] = useState(null); // "publish" | "retry" | "reopen"
	const [syncDetailsOpen, setSyncDetailsOpen] = useState(false);

	const readOnly = meta.status && meta.status !== "DRAFT";
	const canRecoverFailedSync = meta.status === "PUBLISHED" && meta.ginesys_sync === "FAILED" && !meta.ginesys_po_id;
	const canSyncCost = meta.status === "PUBLISHED" && meta.ginesys_sync === "DONE" && !!meta.ginesys_po_id;
	const syncChipValue = meta.ginesys_sync || (meta.status === "PUBLISHED" ? "PENDING" : null);
	const failedSyncMsg = syncErrorMessage(meta.ginesys_last_error);

	const load = async ({ showPage = false } = {}) => {
		if (!id) return;
		setError("");
		if (showPage) setPageLoading(true);
		try {
			const res = await axios.get(`${PO_API}/${id}`);
			const po = res.data.result;
			setMeta({
				status: po.status,
				po_no: po.po_no,
				id: po.id,
				ginesys_sync: po.ginesys_sync,
				ginesys_po_id: po.ginesys_po_id,
				ginesys_scheme_doc_no: po.ginesys_scheme_doc_no,
				ginesys_last_error: po.ginesys_last_error,
			});
			const mapped = mapPoToForm(po);
			const labels = await resolvePoHeaderLabels(mapped);
			setMasterLabels(labels);
			setForm(mapped);
		} catch (err) {
			setError(parseErrorMessage(err));
		} finally {
			if (showPage) setPageLoading(false);
		}
	};

	useEffect(() => {
		load({ showPage: true });
	}, [id]);

	const handleSectionSave = async (nextForm) => {
		const payload = nextForm || form;
		setMessage("");
		setError("");
		try {
			assertRequiredPoHeader(payload);
		} catch (err) {
			setError(err.message);
			throw err;
		}
		setSaving(true);
		setLoadingOpen(true);
		try {
			const res = await axios.put(`${PO_API}/${id}`, payload);
			setMessage("Draft saved");
			setMessageTone("success");
			setMessage("Draft saved");
			const po = res.data.result;
			setMeta({
				status: po.status,
				po_no: po.po_no,
				id: po.id,
				ginesys_sync: po.ginesys_sync,
				ginesys_po_id: po.ginesys_po_id,
				ginesys_scheme_doc_no: po.ginesys_scheme_doc_no,
				ginesys_last_error: po.ginesys_last_error,
			});
			const mapped = mapPoToForm(po);
			const labels = await resolvePoHeaderLabels(mapped);
			setMasterLabels(labels);
			setForm(mapped);
		} catch (err) {
			setError(parseErrorMessage(err));
			throw err;
		} finally {
			setSaving(false);
			setLoadingOpen(false);
		}
	};

	const runPublish = async () => {
		setConfirmAction(null);
		setMessage("");
		setError("");
		try {
			assertRequiredPoHeader(form);
		} catch (err) {
			setError(err.message);
			return;
		}
		setSaving(true);
		setLoadingOpen(true);
		try {
			await axios.put(`${PO_API}/${id}`, form);
			const res = await axios.post(`${PO_API}/${id}/publish`);
			setMessageTone("success");
			setMessage(res.data.message || "Published");
			await load();
		} catch (err) {
			setError(parseErrorMessage(err));
		} finally {
			setSaving(false);
			setLoadingOpen(false);
		}
	};

	const runRetrySync = async () => {
		setConfirmAction(null);
		setSaving(true);
		setLoadingOpen(true);
		setError("");
		setMessage("");
		try {
			const res = await retryPurchaseOrderSyncApi(id);
			setMessageTone("success");
			setMessage(res.data?.message || "Sync re-queued");
			await load();
		} catch (err) {
			setError(parseErrorMessage(err));
		} finally {
			setSaving(false);
			setLoadingOpen(false);
		}
	};

	const runSyncCost = async () => {
		setSaving(true);
		setLoadingOpen(true);
		setError("");
		setMessage("");
		try {
			const res = await syncPurchaseOrderCostApi(id);
			if (res.data?.status === "pending") {
				setMessageTone("warn");
				setMessage(res.data.message || "Cost is not in BigQuery yet");
				return;
			}
			setMessageTone("success");
			setMessage(res.data?.message || "Cost synced");
			await load();
		} catch (err) {
			setError(parseErrorMessage(err));
		} finally {
			setSaving(false);
			setLoadingOpen(false);
		}
	};

	const runReopenForEdit = async () => {
		setConfirmAction(null);
		setSaving(true);
		setLoadingOpen(true);
		setError("");
		setMessage("");
		try {
			const res = await reopenPurchaseOrderForEditApi(id);
			setMessageTone("success");
			await load();
		} catch (err) {
			setError(parseErrorMessage(err));
		} finally {
			setSaving(false);
			setLoadingOpen(false);
		}
	};

	if (!id) {
		return (
			<PoPage>
				<PoPageHeader
					title="Selected purchase order"
					subtitle="Open a row from All purchase orders to view or edit a draft."
				/>
			</PoPage>
		);
	}

	if (pageLoading) return <SelectedPoLoading />;

	return (
		<PoPage sx={{ py: 1.25 }}>
			<PoPageHeader
				compact
				title={
					meta.ginesys_scheme_doc_no || form.ginesys_scheme_doc_no
						? `${meta.ginesys_scheme_doc_no || form.ginesys_scheme_doc_no}`
						: ""
				}
				subtitle={
					<Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap>
						<Typography sx={{ fontSize: 14, color: poColors.muted, fontWeight: 500 }}>
							{meta.po_no || `PO-${id}`}
						</Typography>
					</Stack>
				}
				badge={<PoStatusBadge status={meta.status || "DRAFT"} />}
				actions={
					<>
						{!readOnly && (
							<Button
								size="small"
								variant="contained"
								disabled={saving}
								onClick={() => setConfirmAction("publish")}
								sx={poPrimaryBtnSx}>
								Publish
							</Button>
						)}
						<Typography sx={{ fontSize: 12, fontWeight: 600, color: poColors.muted }}>Ginesys Sync :</Typography>
						{syncChipValue ? (
							<PoGinesysSyncBadge value={syncChipValue} />
						) : (
							<Typography sx={{ fontSize: 12, color: poColors.muted }}>—</Typography>
						)}
						{canSyncCost && (
							<Button size="small" variant="outlined" disabled={saving} onClick={runSyncCost} sx={poSecondaryBtnSx}>
								Sync cost
							</Button>
						)}
						<Button
							size="small"
							variant="text"
							onClick={() => setSyncDetailsOpen(true)}
							sx={{
								...poGhostBtnSx,
								minWidth: 0,
								px: 1,
								py: 0.35,
								fontSize: 12,
								fontWeight: 600,
								textTransform: "none",
							}}>
							View more
						</Button>
					</>
				}
			/>

			{error && (
				<Typography sx={{ mb: 1.5, px: 0.5, fontSize: 13, fontWeight: 600, color: poColors.danger }}>{error}</Typography>
			)}
			{message && (
				<Typography
					sx={{
						mb: 1.5,
						px: 0.5,
						fontSize: 13,
						fontWeight: 600,
						color: messageTone === "warn" ? poColors.warn : poColors.success,
					}}>
					{message}
				</Typography>
			)}

			{canRecoverFailedSync && (
				<Alert
					severity="warning"
					sx={{ mb: 1.5 }}
					action={
						<Stack direction="row" spacing={1} sx={{ alignItems: "center", pr: 0.5 }}>
							<Button
								size="small"
								variant="contained"
								disabled={saving}
								onClick={() => setConfirmAction("retry")}
								sx={poPrimaryBtnSx}>
								Retry sync
							</Button>
							<Button
								size="small"
								variant="outlined"
								disabled={saving}
								onClick={() => setConfirmAction("reopen")}
								sx={poSecondaryBtnSx}>
								Edit &amp; re-publish
							</Button>
						</Stack>
					}>
					<Box>
						<strong>Ginesys sync failed.</strong>
						{failedSyncMsg ? ` ${failedSyncMsg}` : ""} Choose retry (same payload) or edit &amp; re-publish.
					</Box>
				</Alert>
			)}

			<PurchaseOrderForm
				variant="selected"
				value={form}
				onChange={setForm}
				readOnly={readOnly}
				onSectionSave={handleSectionSave}
				saving={saving}
				masterLabels={masterLabels}
			/>

			<PoConfirmDialog
				open={confirmAction === "publish"}
				title="Publish purchase order?"
				message="This PO will be locked and queued for Ginesys sync."
				confirmLabel="Publish"
				loading={saving}
				onCancel={() => setConfirmAction(null)}
				onConfirm={runPublish}
			/>
			<PoConfirmDialog
				open={confirmAction === "retry"}
				title="Retry Ginesys sync?"
				message="This will re-queue the same published payload. Cron will call Ginesys Create PO again."
				confirmLabel="Retry sync"
				loading={saving}
				onCancel={() => setConfirmAction(null)}
				onConfirm={runRetrySync}
			/>
			<PoConfirmDialog
				open={confirmAction === "reopen"}
				title="Edit & re-publish?"
				message="This PO will reopen as a draft so you can fix data and publish again. The failed outbox will not be retried."
				confirmLabel="Reopen as draft"
				loading={saving}
				onCancel={() => setConfirmAction(null)}
				onConfirm={runReopenForEdit}
			/>

			<PoSyncDetailsModal
				open={syncDetailsOpen}
				onClose={() => setSyncDetailsOpen(false)}
				purchaseOrderId={meta.id || id}
				meta={{
					status: meta.status,
					po_no: meta.po_no,
					ginesys_sync: syncChipValue,
					ginesys_po_id: meta.ginesys_po_id,
					ginesys_scheme_doc_no: meta.ginesys_scheme_doc_no,
					ginesys_last_error: meta.ginesys_last_error,
				}}
			/>
		</PoPage>
	);
};

export default SelectedPurchaseOrder;
